import { Checkbox } from '@mui/material';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { getSelectedCatalogItems } from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import { CatalogItemSource } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import {
    removeCustomListItems,
    updateCustomListItem,
} from 'entities/CustomList';
import { fetchOrder } from 'entities/Order';
import { getSelectedOrderId } from 'entities/Order/model/selectors/ordersSelectors';
import { getUserCanManageCatalogItems } from 'entities/User/model/selectors/userPermissionsSeletors';
import { AddCatalogItemWithQty } from 'features/AddCatalogItemWithQty';
import {
    addItemsToOrderService,
    buildSingleAddItemData,
} from 'features/AddItemsToOrder';
import { addItemsToOrderIsCreating } from 'features/AddItemsToOrder/model/selectors/addItemsToOrderSelectors';
import { addItemsToOrderActions } from 'features/AddItemsToOrder/model/slices/addItemsToOrderSlice';
import { editOrderItem } from 'features/editItemsInOrder/model/services/editOrderItem/editOrderItem';
import { EditItemsInOrderRequestParams } from 'features/editItemsInOrder/model/types/editItemsInOrderSchema';
import { deleteOrderItem } from 'features/removeItemsInOrder/model/services/deleteOrderItem/deleteOrderItem';
import { getRouteNotFound } from 'shared/const/router';
import {
    useIsAddItemsFlow,
    useIsListsAddItemsFlow,
} from 'shared/lib/contexts/CatalogIdentityContext';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { useDebouncedCallback } from 'shared/lib/hooks/useDebouncedCallback';

import type { AppDispatch } from 'app/providers/StoreProvider';

interface CatalogItemCheckboxProps {
    isActive: boolean;
    showQtyCheckbox: boolean;
    /** Router state had `selectedOrderId` — gates order API sync + `source` on those requests. */
    quickEdit: boolean;
    isInOrder: boolean;
    qty: number;
    /** Key in `selectedItems` / multiselect map (usually `getItemUid(...)`; see `PropertyCatalogItemDataDefiner`). */
    selectionKey: string;
    /** Actual product uid/uuid */
    itemUid: string;
    /** Property Catalog Id when this row is a Property Catalog item */
    catalogItemId?: string;
    /**
     * Id passed to order APIs: **order line id** when the row is already on the order; otherwise the same
     * stable id as `selectionKey` / `itemUid` (`getItemUid`) so it stays aligned with multiselect keys.
     */
    itemIdToEdit: string;
    isOrderDetails?: boolean;
    disabled?: boolean;
    isAvailable?: boolean;
    source?: CatalogItemSource;
    /** Browse/selection category for `addNewItem` tracking; not the row's property catalog category. */
    categoryId?: string;
    subCategoryId?: string;
    title?: string;
    propertyCategoryId?: string;
    /** Quantity written on the first add. Inventory and property catalog use 1. */
    addQuantity?: number;
    /** Stamped onto `newAddedItems` so list-rail counts can find the row. */
    listId?: string;
}

enum OrderActionType {
    ADD_ITEM = 'add',
    DELETE_ITEM = 'delete',
    EDIT_QTY_ITEM = 'edit',
}

type UpdateOrderForQtyEdit = (
    params: EditItemsInOrderRequestParams,
    action: OrderActionType,
) => void | Promise<void>;

async function syncQuickEditQuantityToOrder(options: {
    dispatch: AppDispatch;
    qty: number;
    id: string;
    itemUid: string;
    selectedOrderId: string | undefined;
    dataToEdit: EditItemsInOrderRequestParams;
    updateOrder: UpdateOrderForQtyEdit;
}): Promise<void> {
    const {
        dispatch,
        qty,
        id,
        itemUid,
        selectedOrderId,
        dataToEdit,
        updateOrder,
    } = options;

    dispatch(addItemsToOrderActions.setIsLoadingAddItemsToOrder(true));
    if (qty >= 1 && qty) {
        if (selectedOrderId) {
            await updateOrder(dataToEdit, OrderActionType.EDIT_QTY_ITEM);
        }
    } else if (qty <= 0) {
        dispatch(catalogItemActions.removeMultiselectItem(id));
        dispatch(catalogItemActions.removeNewItem(itemUid));
        if (selectedOrderId) {
            await updateOrder(dataToEdit, OrderActionType.DELETE_ITEM);
        }
    }
    dispatch(addItemsToOrderActions.setIsLoadingAddItemsToOrder(false));
}

async function toggleSelectAddToMultiselect(options: {
    dispatch: AppDispatch;
    id: string;
    itemUid: string;
    catalogItemId?: string;
    title?: string;
    propertyCategoryId?: string;
    trackNewAddedItems: boolean;
    source?: CatalogItemSource;
    categoryId?: string;
    subCategoryId?: string;
    quickEdit: boolean;
    selectedOrderId: string | undefined;
    dataToEdit: EditItemsInOrderRequestParams;
    updateOrder: UpdateOrderForQtyEdit;
    addQuantity: number;
    listId?: string;
}): Promise<void> {
    const {
        dispatch,
        id,
        itemUid,
        catalogItemId,
        title,
        propertyCategoryId,
        trackNewAddedItems,
        source,
        categoryId,
        subCategoryId,
        quickEdit,
        selectedOrderId,
        dataToEdit,
        updateOrder,
        addQuantity,
        listId,
    } = options;

    dispatch(
        catalogItemActions.addMultiselectItem({
            id,
            uid: itemUid,
            catalogItemId,
            ...(title !== undefined && { title }),
            ...(propertyCategoryId !== undefined && {
                propertyCategoryId,
            }),
        }),
    );

    if (addQuantity !== 1) {
        dispatch(
            catalogItemActions.setItemQuantity({ id, value: addQuantity }),
        );
    }

    if (trackNewAddedItems) {
        dispatch(
            catalogItemActions.addNewItem({
                itemId: id,
                source,
                categoryId,
                subCategoryId,
                listId,
            }),
        );
    }

    if (quickEdit && selectedOrderId) {
        await updateOrder(
            { ...dataToEdit, qty: addQuantity },
            OrderActionType.ADD_ITEM,
        );
    }
}

async function toggleSelectRemoveFromMultiselect(options: {
    dispatch: AppDispatch;
    id: string;
    itemIdToEdit: string;
    trackNewAddedItems: boolean;
    quickEdit: boolean;
    selectedOrderId: string | undefined;
    dataToEdit: EditItemsInOrderRequestParams;
    updateOrder: UpdateOrderForQtyEdit;
}): Promise<void> {
    const {
        dispatch,
        id,
        itemIdToEdit,
        trackNewAddedItems,
        quickEdit,
        selectedOrderId,
        dataToEdit,
        updateOrder,
    } = options;

    dispatch(catalogItemActions.removeMultiselectItem(id));

    if (quickEdit && selectedOrderId) {
        await updateOrder(
            { ...dataToEdit, qty: 0 },
            OrderActionType.DELETE_ITEM,
        );
    }

    if (trackNewAddedItems && id === itemIdToEdit) {
        dispatch(catalogItemActions.removeItem(id));
    }
}

export const CatalogItemCheckbox = ({
    isActive,
    showQtyCheckbox,
    selectionKey,
    itemUid,
    catalogItemId,
    itemIdToEdit,
    quickEdit,
    isInOrder,
    qty,
    isOrderDetails,
    disabled = false,
    isAvailable = true,
    source,
    categoryId,
    subCategoryId,
    title,
    propertyCategoryId,
    addQuantity = 1,
    listId,
}: CatalogItemCheckboxProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { listId: routeListId = '' } = useParams();
    const pendingListQtyRef = useRef<{
        rollback: number;
        next: number;
    } | null>(null);

    const canEditCatalog = useSelector(getUserCanManageCatalogItems);
    const isCreatingOrder = useSelector(addItemsToOrderIsCreating);
    const isControlDisabled = disabled || isCreatingOrder;
    const selectedOrderId = useSelector(getSelectedOrderId);
    const selectedItems = useSelector(getSelectedCatalogItems);
    const trackNewAddedItems = useIsAddItemsFlow();
    const isListsAddItems = useIsListsAddItemsFlow();
    const customListItemId = selectedItems?.[selectionKey]?.customListItemId;

    // Always use itemIdToEdit, unless we are in order edit mode (quickEdit) and it's a property catalog item
    const apiEditId = useMemo(
        () =>
            source === 'marketplace' || !quickEdit || isInOrder
                ? itemIdToEdit
                : catalogItemId ?? itemIdToEdit,
        [source, quickEdit, isInOrder, catalogItemId, itemIdToEdit],
    );

    const [loading, setLoading] = useState(false);

    const redirectOnNotFound = useCallback(
        () => navigate(getRouteNotFound()),
        [navigate],
    );

    const updateOrder = useDebouncedCallback(
        async (
            params: EditItemsInOrderRequestParams,
            action: OrderActionType,
        ) => {
            dispatch(addItemsToOrderActions.setIsLoadingAddItemsToOrder(true));
            setLoading(true);
            switch (action) {
                case OrderActionType.ADD_ITEM: {
                    const orderSource = source === 'lists' ? 'catalog' : source;
                    if (
                        orderSource === 'catalog' ||
                        orderSource === 'inventory' ||
                        orderSource === 'marketplace'
                    ) {
                        await dispatch(
                            addItemsToOrderService({
                                data: buildSingleAddItemData(orderSource, {
                                    itemId: params.itemId,
                                    qty: params.qty,
                                }),
                                orderId: params.orderId,
                            }),
                        );
                    }
                    break;
                }

                case OrderActionType.DELETE_ITEM:
                    await dispatch(deleteOrderItem(params));
                    break;

                case OrderActionType.EDIT_QTY_ITEM:
                    await dispatch(editOrderItem(params));
                    break;
                default:
                    break;
            }
            isOrderDetails &&
                (await dispatch(
                    fetchOrder({
                        id: selectedOrderId,
                        onNotFound: redirectOnNotFound,
                    }),
                ));
            dispatch(addItemsToOrderActions.setIsLoadingAddItemsToOrder(false));
            setLoading(false);
        },
    );

    const syncCustomListQuantity = useDebouncedCallback(
        async (nextQty: number, listItemId: string, selectionId: string) => {
            if (!routeListId || !listItemId) {
                return;
            }

            setLoading(true);

            if (nextQty < 1) {
                const result = await dispatch(
                    removeCustomListItems({
                        listId: routeListId,
                        itemIds: [listItemId],
                    }),
                );
                const pending = pendingListQtyRef.current;
                const isLatestRequest = pending?.next === nextQty;

                if (result.meta.requestStatus === 'fulfilled') {
                    dispatch(
                        catalogItemActions.removeMultiselectItem(selectionId),
                    );
                    if (isLatestRequest) {
                        pendingListQtyRef.current = null;
                    }
                } else if (isLatestRequest && pending) {
                    dispatch(
                        catalogItemActions.setItemQuantity({
                            id: selectionId,
                            value: pending.rollback,
                        }),
                    );
                    pendingListQtyRef.current = null;
                    toast.error(t('customLists.failedToUpdateQuantity'));
                }
            } else {
                const result = await dispatch(
                    updateCustomListItem({
                        listId: routeListId,
                        itemId: listItemId,
                        targetQuantity: nextQty,
                    }),
                );
                const pending = pendingListQtyRef.current;

                if (pending?.next === nextQty) {
                    if (result.meta.requestStatus !== 'fulfilled') {
                        dispatch(
                            catalogItemActions.setItemQuantity({
                                id: selectionId,
                                value: pending.rollback,
                            }),
                        );
                        toast.error(t('customLists.failedToUpdateQuantity'));
                    }
                    pendingListQtyRef.current = null;
                }
            }
            setLoading(false);
        },
    );

    const onItemToggleSelect = useCallback(
        (id: string) => async () => {
            const dataToEdit = {
                orderId: selectedOrderId,
                itemId: apiEditId,
                qty,
                ...(quickEdit && { source }),
            };

            const isSelecting = (!qty || qty === 0) && !isInOrder;

            if (isSelecting) {
                await toggleSelectAddToMultiselect({
                    dispatch,
                    id,
                    itemUid,
                    catalogItemId,
                    title,
                    propertyCategoryId,
                    trackNewAddedItems,
                    source,
                    categoryId,
                    subCategoryId,
                    quickEdit,
                    selectedOrderId,
                    dataToEdit,
                    updateOrder,
                    addQuantity,
                    listId,
                });
            } else {
                await toggleSelectRemoveFromMultiselect({
                    dispatch,
                    id,
                    itemIdToEdit,
                    trackNewAddedItems,
                    quickEdit,
                    selectedOrderId,
                    dataToEdit,
                    updateOrder,
                });
            }
        },
        [
            selectedOrderId,
            apiEditId,
            itemIdToEdit,
            qty,
            isInOrder,
            dispatch,
            itemUid,
            catalogItemId,
            source,
            categoryId,
            subCategoryId,
            quickEdit,
            trackNewAddedItems,
            updateOrder,
            title,
            propertyCategoryId,
            addQuantity,
            listId,
        ],
    );

    const onSetItemValue = useCallback(
        (id: string) => async (nextQty: number) => {
            const dataToEdit = {
                orderId: selectedOrderId,
                itemId: apiEditId,
                qty: nextQty,
                ...(quickEdit && { source }),
            };

            if (nextQty < 1) {
                if (quickEdit && id === itemIdToEdit) {
                    dispatch(catalogItemActions.removeItem(id));
                } else if (!(isListsAddItems && customListItemId)) {
                    dispatch(catalogItemActions.toggleMultiselectItem(id));
                }
            } else {
                dispatch(
                    catalogItemActions.setItemQuantity({
                        id,
                        value: nextQty,
                    }),
                );
            }

            if (isListsAddItems && customListItemId) {
                const pending = pendingListQtyRef.current;
                if (pending) {
                    pending.next = nextQty;
                } else if (nextQty >= 1) {
                    pendingListQtyRef.current = {
                        rollback: qty,
                        next: nextQty,
                    };
                }
                syncCustomListQuantity(nextQty, customListItemId, id);
                return;
            }

            if (quickEdit) {
                await syncQuickEditQuantityToOrder({
                    dispatch,
                    qty: nextQty,
                    id,
                    itemUid,
                    selectedOrderId,
                    dataToEdit,
                    updateOrder,
                });
            } else if (trackNewAddedItems && nextQty <= 0) {
                dispatch(catalogItemActions.removeMultiselectItem(id));
                dispatch(catalogItemActions.removeNewItem(itemUid));
            }
        },
        [
            selectedOrderId,
            apiEditId,
            itemIdToEdit,
            qty,
            quickEdit,
            trackNewAddedItems,
            source,
            dispatch,
            updateOrder,
            itemUid,
            isListsAddItems,
            customListItemId,
            syncCustomListQuantity,
        ],
    );

    return !showQtyCheckbox ? (
        <Checkbox
            disableRipple
            disabled={isControlDisabled}
            sx={{
                display: canEditCatalog ? 'inline-flex' : 'none',
                paddingTop: '0px',
                '& >.MuiSvgIcon-root': {
                    width: '28px',
                    height: '28px',
                },
            }}
            checked={isActive}
            onClick={
                isControlDisabled ? undefined : onItemToggleSelect(itemIdToEdit)
            }
        />
    ) : (
        <AddCatalogItemWithQty
            value={qty}
            active={isActive}
            onItemToggleSelect={onItemToggleSelect(itemUid)}
            onSetValue={onSetItemValue(selectionKey)}
            loading={loading || isControlDisabled}
            disabledAddButton={!isAvailable}
        />
    );
};
